'use client'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import Swiper from '@src/quince-components/src/atoms/swiper'
import { DOTS_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import productService from '@src/quince-site-common/src/services/productService'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { SectionProps } from '@src/SectionRenderer/SectionRenderer.types'

import styles from './newArrivalProductList.module.scss'

const NewArrivalProductList = (props: SectionProps) => {
  const { partnerProduct } = props.sectionConfig
  const deviceInfo = useDeviceInfo()
  const { t } = useTranslation('landing')
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const {
    fetchData: fetchProductCards,
    isLoading,
    hasError,
    error,
    response: productCards,
  } = useFetch<IProductCard[], Parameters<typeof productService.getProductCards>>(productService.getProductCards, {
    initialData: [],
  })

  useEffect(() => {
    const recommendedContentfulProductIds = partnerProduct?.newArrivalProducts?.map(x => x.contentful_id)
    if (recommendedContentfulProductIds?.length)
      void fetchProductCards({ contentfulProductIds: recommendedContentfulProductIds }, isRecurringItemSubscriptionsExpEnabled)
  }, [])

  useEffect(() => {
    if (hasError) {
      captureException(error, {
        module: MODULES.PARTNER_PRODUCT_LIST,
        feature: FEATURES.NEW_ARRIVAL_PRODUCT_LIST,
      })
    }
  }, [hasError, error])

  if ((partnerProduct?.newArrivalProducts || []).length === 0) return null
  if (!isLoading && !productCards?.length) return null

  return (
    <div className={styles.newArrivalProductListContainer}>
      <h1 className={styles.newArrivalProductListContainer__title}>{t('best_sellers')}</h1>
      {!isLoading && (
        <Swiper
          id="new-arrival-product-list"
          className={styles.productList}
          numberOfVisibleSlides={4}
          variableWidth={!isDesktop}
          showNavigator={isDesktop}
          dotsPosition={DOTS_POSITION.BOTTOM}
        >
          {productCards?.map((productCard, idx) => {
            const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
            return (
              <ProductCard
                key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                productCard={productCard}
                maxWidth={maxWidth}
                className={styles.productCard}
                tracker={`new_arrival__${idx}`}
              />
            )
          })}
        </Swiper>
      )}
    </div>
  )
}

export default NewArrivalProductList
